import { compareIds } from './messages'
import type { ChatConversation, ChatMessage } from '../types'

/**
 * 会话列表上的纯改写（ChatProvider 收到实时事件时调用），不改入参、返回新数组。
 * 与 main-mobile src/features/chat/utils/conversationCache.ts 同一套规则。
 * 实时事件可能重复到达（Centrifugo 闪断重连先补发漏掉的 publication，随后 connected
 * 又触发 REST 补拉），这里必须幂等。
 */

export interface MessageCreatedResult {
  list: ChatConversation[]
  /** 列表里有这个会话（没有 = 调用方重拉列表） */
  found: boolean
  /** 这条消息改了列表（新的一条）；重复 / 更旧的一条为 false */
  applied: boolean
}

/**
 * 新消息并进会话列表：更新最新一条、未读数、把会话挪到最前。
 *
 * - 同一条（id 相同）或比列表里最新一条还旧的：什么都不改（不重复加未读、最新一条不回退）。
 * - 未读 +1 只给别人发的、不是系统消息、且我没正开着这个会话的。系统消息不计未读。
 * - 我自己发的（文字 / 图片）：未读清零（我都发言了，说明前面的看过了）。
 */
export function applyMessageCreated(
  list: ChatConversation[],
  conversationId: string,
  msg: ChatMessage,
  me: string | null | undefined,
  viewing: boolean,
): MessageCreatedResult {
  const at = list.findIndex((c) => c.id === conversationId)
  if (at < 0) return { list, found: false, applied: false }
  const conv = list[at]
  if (conv.lastMessage && compareIds(conv.lastMessage.id, msg.id) >= 0) return { list, found: true, applied: false }
  let unreadCount = conv.unreadCount
  if (msg.kind !== 'system') {
    const mine = msg.senderId === me
    if (!mine && !viewing) unreadCount += 1
    if (mine) unreadCount = 0
  }
  const next: ChatConversation = { ...conv, lastMessage: msg, lastMessageAt: msg.createdAt, unreadCount }
  return { list: [next, ...list.slice(0, at), ...list.slice(at + 1)], found: true, applied: true }
}

/** 某人的已读位置前进了：改成员的 lastReadMessageId；是我自己且读到了最新一条时未读清零。 */
export function applyConversationRead(
  list: ChatConversation[],
  conversationId: string,
  userId: string,
  lastReadMessageId: string,
  me: string | null | undefined,
): ChatConversation[] {
  const at = list.findIndex((c) => c.id === conversationId)
  if (at < 0) return list
  const conv = list[at]
  const members = conv.members.map((m) =>
    m.user.id === userId ? { ...m, lastReadMessageId: lastReadMessageId as typeof m.lastReadMessageId } : m,
  )
  const readAll = userId === me && !!conv.lastMessage && compareIds(lastReadMessageId, conv.lastMessage.id) >= 0
  const next = { ...conv, members, unreadCount: readAll ? 0 : conv.unreadCount }
  return [...list.slice(0, at), next, ...list.slice(at + 1)]
}

/** 我已不在这个会话里（被移出 / 退群）：从列表删掉。 */
export function removeConversation(list: ChatConversation[], conversationId: string): ChatConversation[] {
  return list.some((c) => c.id === conversationId) ? list.filter((c) => c.id !== conversationId) : list
}

/** 用服务端返回的一个会话替换列表里的同一个（改名 / 加人后）；不在列表里就放到最前。 */
export function upsertConversation(list: ChatConversation[], conversation: ChatConversation): ChatConversation[] {
  const at = list.findIndex((c) => c.id === conversation.id)
  if (at < 0) return [conversation, ...list]
  return [...list.slice(0, at), conversation, ...list.slice(at + 1)]
}
